import React, { useEffect, useMemo, useState } from 'react';
import { Platform, KeyboardAvoidingView, ScrollView } from 'react-native';

import { Button } from '../../../components/Interface/Forms/Buttons/Button';
import { Header } from '../../../components/Interface/Headers/Header';
import { Titles } from '../../../components/Interface/Typographies/Titles';
import {
  Container,
  Content,
  Form,
  Footer,
  Section,
  JustificativaContainer,
  JustificativaInput,
  JustificativaLabel,
  ContentHeader,
  InputGroup,
  RadioOptionsList,
  RadioOptionItem,
  RadioOptionIcon,
  RadioOptionTextContainer,
  RadioOptionLabel,
  RadioOptionDescription,
} from './styles';

import { NativeStackScreenProps } from 'react-native-screens/lib/typescript/native-stack/types';
import { RootStackParamList } from '../../../dtos/RoutesDTO';

import { ReembolsoItem } from '../../../components/shared/Reembolsos/ReembolsoItem';
import { api } from '../../../services/api';
import { SafeAreaView } from 'react-native-safe-area-context';
import { RFValue } from 'react-native-responsive-fontsize';
import { useRemoteConfig } from '../../../context/useRemoteConfig';
import { RefundTermsModal } from '../../../components/shared/RefundTermsModal';

type ScreenProps = NativeStackScreenProps<
  RootStackParamList,
  'ConfirmaReembolso'
>;

type RefundReasonOption = {
  label: string;
  value: string;
  description?: string;
};

const REASON_OTHER = 'Outros';
const RADIO_ACTIVE_OPACITY = 0.7;
const SCROLL_PADDING_BOTTOM = RFValue(20);
const BUTTON_TEXT_LOADING = 'Enviando...';
const BUTTON_TEXT_DEFAULT = 'Continuar';

export function ConfirmaReembolso({ navigation, route }: ScreenProps) {
  const {
    item,
    banco,
    agencia,
    conta,
    chavePix,
    cpfFavorecido,
    digitoAgencia,
    digitoConta,
    nomeFavorecido,
    tipoChavePix,
    tipoInscricao,
    tipoConta,
  } = route.params;

  const {
    prazoPixReembolso,
    reembolsoTextoFinal,
    isLoading: loadingRemoteConfigs,
  } = useRemoteConfig(true);

  const [isLoading, setIsLoading] = useState(false);
  const [showTermsModal, setShowTermsModal] = useState(false);

  const [refundReasonOptions, setRefundReasonOptions] = useState([]);
  const [apiError, setApiError] = useState(false);
  const [freeTextReason, setFreeTextReason] = useState('');

  useEffect(() => {
    let isMounted = true;

    const loadRefundReasons = async () => {
      try {
        const response = await api.get('/motivos_reembolso');

        const { motivos, descricao_motivos } = response.data;

        const options = motivos.map((label: string, index: number) => ({
          label,
          value: label,
          description: descricao_motivos[index] ?? undefined,
        }));

        if (isMounted) {
          setRefundReasonOptions(options);
        }
      } catch (err) {
        if (isMounted) {
          setApiError(true);
        }
      }
    };

    loadRefundReasons();

    return () => {
      isMounted = false;
    };
  }, []);

  const [selectedReason, setSelectedReason] = useState<string | undefined>(
    undefined,
  );
  const [otherReason, setOtherReason] = useState('');

  const isOtherSelected = selectedReason === REASON_OTHER;

  const justificativaFinal = useMemo(() => {
    if (!selectedReason) {
      return '';
    }

    if (isOtherSelected) {
      const trimmed = otherReason.trim();
      return trimmed ? `${REASON_OTHER}: ${trimmed}` : '';
    }

    return selectedReason;
  }, [selectedReason, isOtherSelected, otherReason]);

  const canContinue = apiError
    ? !!freeTextReason.trim()
    : !!justificativaFinal;

  const handleReasonChange = (value: string) => {
    if (!value || value === 'select') {
      setSelectedReason(undefined);
      setOtherReason('');
      return;
    }

    setSelectedReason(value);

    if (value !== REASON_OTHER) {
      setOtherReason('');
    }
  };

  async function handleReembolso() {
    if (isLoading) {
      return;
    }

    try {
      setIsLoading(true);

      await api.post('/enviaReembolsoRemessa', {
        banco,
        agencia,
        conta,
        chavePix,
        compra_id: item.id,
        justificativa: apiError ? freeTextReason.trim() : justificativaFinal,
        tipo_inscricao: tipoInscricao,
        nome_favorecido: nomeFavorecido,
        cpf_favorecido: cpfFavorecido,
        digito_conta: digitoConta,
        tipo_conta: tipoConta,
        digito_agencia: digitoAgencia,
        tipo_chavePix: tipoChavePix,
      });

      navigation.navigate('ConfirmationScreen', {
        title: 'Solicitação de reembolso enviada',
        type: 'success',
        text: 'Recebemos sua solicitação e nossa equipe irá analisá-la. Você pode acompanhar o status do reembolso diretamente no app.',
        buttonAction: () => navigation.navigate('PedidosReembolso'),
        buttonText: 'Ver minhas solicitações',
        closeIconAction: () => navigation.navigate('Initial'),
      });
    } catch ({ response }: any) {
      navigation.navigate('ConfirmationScreen', {
        title: 'Não foi possível solicitar o reembolso',
        type: 'error',
        text:
          response?.data?.error ??
          'Ocorreu um problema ao processar sua solicitação. Por favor, tente novamente mais tarde ou entre em contato com o suporte.',
        buttonAction: () => navigation.goBack(),
        buttonText: 'Revisar solicitação',
        closeIconAction: () => navigation.navigate('Initial'),
      });
    } finally {
      setIsLoading(false);
    }
  }

  const handleContinue = () => {
    if (isLoading) {
      return;
    }

    setShowTermsModal(true);
  };

  const handleConfirmTerms = () => {
    if (isLoading) {
      return;
    }

    setShowTermsModal(false);
    handleReembolso();
  };

  return (
    <>
      <RefundTermsModal
        visible={showTermsModal}
        onClose={() => !isLoading && setShowTermsModal(false)}
        onConfirm={handleConfirmTerms}
        prazoPixReembolso={prazoPixReembolso.toString()}
        loadingRemoteConfigs={loadingRemoteConfigs}
        textoFinal={reembolsoTextoFinal}
      />

      <SafeAreaView edges={['bottom']} style={{ flex: 1 }}>
        <Header icon="chevron-left" />

        <KeyboardAvoidingView
          style={{ flex: 1 }}
          behavior={Platform.OS === 'ios' ? 'padding' : 'height'}
          keyboardVerticalOffset={0}>
          <ScrollView
            showsVerticalScrollIndicator={false}
            contentContainerStyle={{
              paddingBottom: SCROLL_PADDING_BOTTOM,
            }}
            keyboardShouldPersistTaps="handled">
            <Container>
              <Content>
                <ContentHeader>
                  <Titles
                    title="Confirme seu pedido de reembolso"
                    subtitle="Revise os detalhes abaixo antes de prosseguir com sua solicitação."
                  />
                </ContentHeader>

                <Section>
                  <ReembolsoItem
                    data={{
                      checked: true,
                      index: 0,
                      date: item.orderDate,
                      formaPagamento: item.paymentMethod,
                      identificador: item.id,
                      installments: item.installment,
                      produto: item.produto,
                      valorTotal: item.totalValue,
                      justificativaDesconto: item.justificativa_desconto,
                      quantidadeCupons: item.quantidadeCupons,
                      quantidadeCuponsUsados: item.quantidadeCuponsUsados,
                      valorReembolos: item.valorReembolso,
                    }}
                  />
                </Section>

                <Form>
                  <InputGroup>
                    <JustificativaLabel>* Motivo de reembolso:</JustificativaLabel>

                    {apiError ? (
                      <JustificativaContainer>
                        <JustificativaInput
                          placeholder="Descreva o motivo do reembolso"
                          autoFocus
                          value={freeTextReason}
                          onChangeText={setFreeTextReason}
                        />
                      </JustificativaContainer>
                    ) : (
                      <RadioOptionsList>
                        {refundReasonOptions.map((option: RefundReasonOption) => {
                          const isSelected = selectedReason === option.value;
                          return (
                            <RadioOptionItem
                              key={option.value}
                              selected={isSelected}
                              onPress={() => handleReasonChange(option.value)}
                              activeOpacity={RADIO_ACTIVE_OPACITY}>
                              <RadioOptionIcon
                                name={isSelected ? 'check-circle' : 'circle-o'}
                                checked={isSelected}
                              />
                              <RadioOptionTextContainer>
                                <RadioOptionLabel>{option.label}</RadioOptionLabel>
                                {!!option.description && (
                                  <RadioOptionDescription>{option.description}</RadioOptionDescription>
                                )}
                              </RadioOptionTextContainer>
                            </RadioOptionItem>
                          );
                        })}
                      </RadioOptionsList>
                    )}
                  </InputGroup>

                  {isOtherSelected && (
                    <InputGroup>
                      <JustificativaLabel>* Detalhe do motivo:</JustificativaLabel>
                      <JustificativaContainer>
                        <JustificativaInput
                          placeholder="Descreva o motivo do reembolso"
                          autoFocus
                          value={otherReason}
                          onChangeText={setOtherReason}
                        />
                      </JustificativaContainer>
                    </InputGroup>
                  )}
                </Form>
              </Content>
            </Container>
          </ScrollView>

          <Footer>
            <Button
              title={isLoading ? BUTTON_TEXT_LOADING : BUTTON_TEXT_DEFAULT}
              enabled={canContinue && !isLoading}
              onPress={handleContinue}
            />
          </Footer>
        </KeyboardAvoidingView>
      </SafeAreaView>
    </>
  );
}
